import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Typography,
	Button,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	MenuItem,
	IconButton,
	Snackbar,
	Alert,
	CircularProgress,
	Chip,
	AlertColor,
	FormControl,
	InputLabel,
	Select,
	Tooltip,
} from '@mui/material';
import { Add, Close, EditNotifications } from '@mui/icons-material';
import { format } from 'date-fns';
import { GridDeleteIcon } from '@mui/x-data-grid';
import {
	deleteOurevents,
	OureventsGET,
	OureventsPOst,
	updateOurevents,
} from '../../../services/api/OurEvent';
import { getAllEvent, GetAllMenuTypes } from '../../../services/api/Event';

interface MenuType {
	_id: string;
	menuType: string;
	status: number;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	__v: number;
}

interface EventType {
	_id: string;
	eventType: string;
	status: number;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	__v: number;
}

interface Event {
	_id: string;
	eventType: string;
	menuType: string;
	eventDate: string;
	address: string;
	locationLink: string;
	description?: string;
	status: number;
}

interface FormData {
	eventType: string;
	menuType: string;
	eventDate: string;
	address: string;
	locationLink: string;
	description: string;
}

interface SnackbarState {
	open: boolean;
	message: string;
	severity: AlertColor;
}

const OurEventsAdmin = () => {
	const [events, setEvents] = useState<Event[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
	const [snackbar, setSnackbar] = useState<SnackbarState>({
		open: false,
		message: '',
		severity: 'success',
	});
	const [isProcessing, setIsProcessing] = useState<boolean>(false);
	const [eventTypes, setEventTypes] = useState<EventType[]>([]);
	const [menuTypes, setMenuTypes] = useState<MenuType[]>([]);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

	const [formData, setFormData] = useState<FormData>({
		eventType: '',
		menuType: '',
		eventDate: '',
		address: '',
		locationLink: '',
		description: '',
	});

	const fetchEvents = async () => {
		try {
			setLoading(true);
			const response = await OureventsGET();
			setEvents(response.data.ourEvents);
		} catch (error) {
			console.error('Error fetching events:', error);
			showSnackbar('Failed to fetch events', 'error');
		} finally {
			setLoading(false);
		}
	};

	const fetchMenuTypes = async () => {
		try {
			const response = await GetAllMenuTypes();
			setMenuTypes(response?.data?.menuTypes || []);
		} catch (error) {
			console.error('Failed to fetch menu types', error);
			showSnackbar('Failed to fetch menu types', 'error');
		}
	};

	const loadEventTypes = useCallback(async () => {
		try {
			const response = await getAllEvent();
			setEventTypes(response.data.eventTypes || []);
		} catch (error) {
			console.error('Failed to load event types:', error);
			showSnackbar('Failed to load event types', 'error');
		}
	}, []);

	useEffect(() => {
		fetchEvents();
		fetchMenuTypes();
		loadEventTypes();
	}, [loadEventTypes]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleMenuTypeChange = (e: any) => {
		setFormData((prev) => ({
			...prev,
			menuType: e.target.value,
		}));
	};

	const handleOpenCreate = () => {
		setCurrentEvent(null);
		setFormData({
			eventType: '',
			menuType: '',
			eventDate: '',
			address: '',
			locationLink: '',
			description: '',
		});
		setOpenDialog(true);
	};

	const handleOpenEdit = (event: Event) => {
		setCurrentEvent(event);
		setFormData({
			eventType: event.eventType,
			menuType: event.menuType,
			eventDate: format(new Date(event.eventDate), 'yyyy-MM-dd'),
			address: event.address,
			locationLink: event.locationLink,
			description: event.description || '',
		});
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleSubmit = async () => {
		try {
			setIsProcessing(true);
			const payload = {
				...formData,
				eventDate: new Date(formData.eventDate).toISOString(),
			};

			if (currentEvent) {
				await updateOurevents(payload, currentEvent._id);
				showSnackbar('Event updated successfully', 'success');
			} else {
				await OureventsPOst(payload);
				showSnackbar('Event created successfully', 'success');
			}

			fetchEvents();
			setOpenDialog(false);
		} catch (error) {
			console.error('Error saving event:', error);
			showSnackbar(`Failed to ${currentEvent ? 'update' : 'create'} event`, 'error');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleOpenDeleteDialog = (event: Event) => {
		setEventToDelete(event);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setEventToDelete(null);
	};

	const handleConfirmDelete = async () => {
		if (eventToDelete) {
			try {
				setIsProcessing(true);
				await deleteOurevents(eventToDelete._id);
				showSnackbar('Event deleted successfully', 'success');
				fetchEvents();
			} catch (error) {
				console.error('Error deleting event:', error);
				showSnackbar('Failed to delete event', 'error');
			} finally {
				setIsProcessing(false);
				handleCloseDeleteDialog();
			}
		}
	};

	const showSnackbar = (message: string, severity: AlertColor) => {
		setSnackbar({ open: true, message, severity });
	};

	const handleCloseSnackbar = () => {
		setSnackbar((prev) => ({ ...prev, open: false }));
	};

	return (
		<Box sx={{ p: 3 }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 3,
				}}
			>
				<Typography variant="h4" fontWeight="bold"></Typography>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={handleOpenCreate}
					sx={{ borderRadius: '8px' }}
				>
					Add Event
				</Button>
			</Box>

			<Paper elevation={3} sx={{ p: 2, borderRadius: '12px' }}>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Event Type</TableCell>
									<TableCell>Menu Type</TableCell>
									<TableCell>Event Date</TableCell>
									<TableCell>Address</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{events.map((event) => (
									<TableRow key={event._id}>
										<TableCell>{event.eventType}</TableCell>
										<TableCell>{event.menuType}</TableCell>
										<TableCell>
											{format(new Date(event.eventDate), 'MMM dd, yyyy')}
										</TableCell>
										<TableCell
											sx={{
												maxWidth: '200px',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{event.address}
										</TableCell>
										<TableCell>
											<Chip
												label={event.status === 1 ? 'Active' : 'Inactive'}
												color={event.status === 1 ? 'success' : 'error'}
												size="small"
											/>
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex' }}>
												<Tooltip title="Edit">
													<IconButton
														onClick={() => handleOpenEdit(event)}
														size={'medium'}
													>
														<EditNotifications fontSize={'medium'} />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton
														onClick={() =>
															handleOpenDeleteDialog(event)
														}
														size={'medium'}
													>
														<GridDeleteIcon fontSize={'medium'} />
													</IconButton>
												</Tooltip>
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>

			<Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
				<DialogTitle>
					{currentEvent ? 'Edit Event' : 'Add New Event'}
					<IconButton
						aria-label="close"
						onClick={handleCloseDialog}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<Box component="form" sx={{ mt: 1 }}>
						<FormControl fullWidth margin="normal">
							<InputLabel>Event Type</InputLabel>
							<Select
								value={formData.eventType}
								onChange={(e) =>
									setFormData({ ...formData, eventType: e.target.value })
								}
								label="Event Type"
								required
							>
								{eventTypes.map((type) => (
									<MenuItem key={type._id} value={type.eventType}>
										{type.eventType}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth margin="normal">
							<InputLabel>Menu Type</InputLabel>
							<Select
								value={formData.menuType}
								onChange={handleMenuTypeChange}
								label="Menu Type"
								required
							>
								{menuTypes.map((type) => (
									<MenuItem key={type._id} value={type.menuType}>
										{type.menuType}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<TextField
							fullWidth
							margin="normal"
							label="Event Date"
							name="eventDate"
							type="date"
							InputLabelProps={{ shrink: true }}
							value={formData.eventDate}
							onChange={handleInputChange}
							required
						/>

						<TextField
							fullWidth
							margin="normal"
							label="Address"
							name="address"
							value={formData.address}
							onChange={handleInputChange}
							required
						/>

						<TextField
							fullWidth
							margin="normal"
							label="Location Link"
							name="locationLink"
							value={formData.locationLink}
							onChange={handleInputChange}
							required
						/>

						<TextField
							fullWidth
							margin="normal"
							label="Description"
							name="description"
							value={formData.description}
							onChange={handleInputChange}
							multiline
							rows={4}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog} disabled={isProcessing}>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						disabled={isProcessing}
						startIcon={isProcessing ? <CircularProgress size={20} /> : null}
					>
						{currentEvent ? 'Update' : 'Create'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						Are you sure you want to delete this event? This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeleteDialog} color="primary">
						Cancel
					</Button>
					<Button onClick={handleConfirmDelete} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default OurEventsAdmin;
